/**
 * ReconnectOverlay — small corner pill shown while the wsClient is
 * mid-handshake or backing off after a dropped WebSocket.
 *
 * Previous design was a full-screen curtain that interrupted whatever
 * the user was doing on every brief drop. New design is a tiny pill in
 * the top-right that never blocks the UI: the lobby roster / map / etc.
 * underneath stays visible and clickable. After STALE_AFTER_MS of
 * sustained reconnection failure the pill escalates to a hard "Connection
 * lost — refresh" prompt with a button so the user has a clear escape
 * hatch instead of an infinite spinner.
 */

import { useEffect, useState } from 'react';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { useReconnect } from '../../hooks/useReconnect';

/** Sustained-disconnect threshold before the pill escalates to a refresh prompt. */
const STALE_AFTER_MS = 8000;

export function ReconnectOverlay() {
  const { state } = useReconnect();
  const [stale, setStale] = useState(false);

  const visible = state === 'reconnecting' || state === 'connecting';

  useEffect(() => {
    if (!visible) {
      setStale(false);
      return;
    }
    const t = window.setTimeout(() => setStale(true), STALE_AFTER_MS);
    return () => {
      window.clearTimeout(t);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        padding: '10px 14px',
        background: stale ? surfaces.waxRed : `${surfaces.slate}E6`,
        border: `1px solid ${stale ? surfaces.waxRed : surfaces.chalk}33`,
        borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: fontStacks.mono,
        fontSize: 11,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: surfaces.chalk,
        pointerEvents: stale ? 'auto' : 'none',
      }}
    >
      {stale ? (
        <>
          <span>Connection lost</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '4px 10px',
              background: surfaces.chalk,
              color: surfaces.waxRed,
              border: 0,
              borderRadius: 4,
              fontFamily: fontStacks.mono,
              fontSize: 10,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </>
      ) : (
        <>
          <span style={{ display: 'inline-block', animation: 'qd-pulse 1.4s ease-in-out infinite' }}>
            ●
          </span>
          <span>Reconnecting</span>
          <style>{`@keyframes qd-pulse { 0%,100% { opacity: 0.4 } 50% { opacity: 1 } }`}</style>
        </>
      )}
    </div>
  );
}
